import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Drawer,
  Flex,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Input,
  VStack,
  useDisclosure,
  useColorModeValue,
  FormControl,
  FormLabel,
  Textarea,
  Spinner,
  useToast,
  Badge,
  Checkbox,
  Tooltip,
} from "@chakra-ui/react";
import {
  AddInventoryOutput,
  GetInventoryOutputs,
  ApproveInventoryOutput,
  GenerateOutputTemplate,
} from "../../../../InventoryServices/InventoryOutputAPI/InventoryOutput";
import ApprovalModal from "../ApprovalModal";
import MediaPreviewModal from "../MediaPreviewModal";

// Maps API approval_status value to Badge colorScheme + display label
const STATUS_META = {
  pending: { label: "Pending", colorScheme: "yellow" },
  approved: { label: "Approved", colorScheme: "green" },
  declined: { label: "Rejected", colorScheme: "red" },
  rejected: { label: "Rejected", colorScheme: "red" },
};

const getStatusMeta = (status) =>
  STATUS_META[status] || { label: status || "Pending", colorScheme: "yellow" };

const OutputSection = ({ versionId, sourceApproved = true }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [file, setFile] = useState(null);
  const [comment, setComment] = useState("");
  const [outputData, setOutputData] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Row selection for "Send to Client"
  const [selectedIds, setSelectedIds] = useState([]);
  const [sending, setSending] = useState(false);

  // Approval modal state
  const {
    isOpen: isApprovalOpen,
    onOpen: onApprovalOpen,
    onClose: onApprovalClose,
  } = useDisclosure();
  const [selectedOutput, setSelectedOutput] = useState(null);
  const [approving, setApproving] = useState(false);

  // Preview modal state
  const {
    isOpen: isPreviewOpen,
    onOpen: onPreviewOpen,
    onClose: onPreviewClose,
  } = useDisclosure();
  const [previewFile, setPreviewFile] = useState(null);

  const headerBg = useColorModeValue("blue.500", "blue.600");
  const headerColor = useColorModeValue("white", "gray.100");
  const rowHoverBg = useColorModeValue("blue.50", "gray.700");
  const tableBg = useColorModeValue("white", "gray.800");

  // Fetch outputs
  const fetchOutputs = useCallback(async () => {
    if (!versionId) return;
    setLoading(true);
    try {
      const outputs = await GetInventoryOutputs(versionId);
      setOutputData(outputs);
    } catch (err) {
      console.error("Error fetching outputs:", err);
      toast({
        title: "Failed to load outputs",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [versionId, toast]);

  useEffect(() => {
    fetchOutputs();
  }, [fetchOutputs]);

  // Reset selection whenever the underlying data changes
  useEffect(() => {
    setSelectedIds([]);
  }, [outputData]);

  // Add Output
  const handleAdd = async () => {
    if (!versionId) {
      toast({
        title: "Version not found",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    if (!file) {
      toast({
        title: "File required",
        description: "Please select a file to upload.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      const payload = new FormData();
      payload.append("media", file);
      if (comment.trim()) {
        payload.append("comment", comment);
      }

      await AddInventoryOutput(versionId, payload);

      setFile(null);
      setComment("");
      onClose();

      toast({
        title: "Output added successfully!",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      await fetchOutputs();
    } catch (err) {
      console.error("Error adding output:", err);
      toast({
        title: "Failed to add output",
        description: err?.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Open the preview modal for an output file
  const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];

  const handleOpenPreview = (out) => {
    const url = out.file_path || out.url;
    if (!url) return;
    const ext = (out.extension || "").toLowerCase();
    const isImage =
      (out.file_type || "").toLowerCase().startsWith("image") ||
      IMAGE_EXTENSIONS.includes(ext);
    setPreviewFile({
      url,
      title: `${out.original_name || "Output"}${ext ? "." + ext : ""}`,
      isImage,
    });
    onPreviewOpen();
  };

  // Checkbox handling
  const toggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  const allSelected =
    outputData?.length > 0 && selectedIds.length === outputData.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds((outputData || []).map((o) => o.id));
    }
  };

  // Send to Client
  const handleSendToClient = async () => {
    if (!versionId || selectedIds.length === 0) return;
    setSending(true);
    try {
      await GenerateOutputTemplate(versionId, selectedIds);
      toast({
        title: "Sent to client successfully!",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setSelectedIds([]);
    } catch (err) {
      console.error("Error sending to client:", err);
      toast({
        title: "Failed to send to client",
        description: err?.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  // Open approval modal for a specific output row
  const handleOpenApproval = (out) => {
    setSelectedOutput(out);
    onApprovalOpen();
  };

  // Submit approve/reject decision for the selected output
  const handleApprovalSubmit = async (approval_status, approval_comment) => {
    if (!selectedOutput || !versionId) return;
    setApproving(true);
    try {
      await ApproveInventoryOutput(versionId, selectedOutput.id, {
        approval_status,
        approval_comment,
      });
      toast({
        title:
          approval_status === "approved" ? "Output approved" : "Output rejected",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onApprovalClose();
      setSelectedOutput(null);
      await fetchOutputs();
    } catch (err) {
      console.error("Error updating approval status:", err);
      toast({
        title: "Failed to update status",
        description: err?.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setApproving(false);
    }
  };

  const isAddOutputDisabled = !sourceApproved;

  return (
    <Box
      bg={tableBg}
      p={6}
      borderRadius="md"
      border="1px solid"
      borderColor="gray.200"
      shadow="sm"
      w="100%"
      minW={0}
    >
      <Flex
        justify="space-between"
        align={{ base: "flex-start", sm: "center" }}
        direction={{ base: "column", sm: "row" }}
        gap={3}
        mb={4}
      >
        <Text fontSize="lg" fontWeight="semibold" color="gray.700">
          Output Table
        </Text>
        <Flex gap={3} flexWrap="wrap" w={{ base: "100%", sm: "auto" }}>
          <Button
            colorScheme="teal"
            size="sm"
            onClick={handleSendToClient}
            isDisabled={selectedIds.length === 0}
            isLoading={sending}
            loadingText="Sending..."
            flexShrink={0}
          >
            Send to Client
          </Button>
          <Tooltip
            label="Source must be approved before adding an output"
            isDisabled={!isAddOutputDisabled}
          >
            <Button
              colorScheme="blue"
              size="sm"
              onClick={onOpen}
              isDisabled={isAddOutputDisabled}
              flexShrink={0}
            >
              + Add Output
            </Button>
          </Tooltip>
        </Flex>
      </Flex>

      {/* Table */}
      {loading && outputData.length === 0 ? (
        <Flex justify="center" py={8}>
          <Spinner size="lg" color="blue.500" />
        </Flex>
      ) : (
        <TableContainer
          overflowX="auto"
          shadow="sm"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          w="100%"
          maxW="100%"
        >
          <Table variant="simple" size="md" tableLayout="fixed" w="100%">
            <Thead bg={headerBg}>
              <Tr>
                <Th color={headerColor} fontSize="sm" w="40px">
                  <Checkbox
                    isChecked={allSelected}
                    onChange={toggleSelectAll}
                    isDisabled={!outputData || outputData.length === 0}
                    colorScheme="whiteAlpha"
                  />
                </Th>
                <Th color={headerColor} fontSize="sm" w="40px">
                  #
                </Th>
                <Th color={headerColor} fontSize="sm" w="140px">
                  File Name
                </Th>
                <Th color={headerColor} fontSize="sm" w="auto">
                  Comment
                </Th>
                <Th color={headerColor} fontSize="sm" w="150px">
                  Created At
                </Th>
                <Th color={headerColor} fontSize="sm" w="100px">
                  Status
                </Th>
                <Th color={headerColor} fontSize="sm" w="90px">
                  Action
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {outputData?.length > 0 ? (
                outputData.map((out, idx) => {
                  const statusMeta = getStatusMeta(out.approval_status);
                  return (
                    <Tr
                      key={out.id || idx}
                      _hover={{
                        bg: rowHoverBg,
                        transform: "scale(1.01)",
                        boxShadow: "md",
                        transition: "all 0.2s",
                        cursor: "pointer",
                      }}
                    >
                      <Td>
                        <Checkbox
                          isChecked={selectedIds.includes(out.id)}
                          onChange={() => toggleSelectRow(out.id)}
                        />
                      </Td>
                      <Td>{idx + 1}</Td>
                      <Td fontWeight="600">
                        {out.original_name ? (
                          <Tooltip
                            label={`${out.original_name}.${out.extension || "file"}`}
                            hasArrow
                            placement="top"
                            openDelay={300}
                          >
                            <Box
                              as="button"
                              type="button"
                              onClick={() => handleOpenPreview(out)}
                              display="block"
                              w="100%"
                              overflow="hidden"
                              textAlign="left"
                              bg="transparent"
                              border="none"
                              p={0}
                              cursor="pointer"
                              _hover={{ textDecoration: "underline" }}
                            >
                              <Text
                                color="blue.600"
                                fontWeight="600"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                              >
                                {out.original_name}.{out.extension || "file"}
                              </Text>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Text color="gray.800">File</Text>
                        )}
                      </Td>
                      <Td fontSize="sm">
                        {out.comment ? (
                          <Tooltip
                            label={out.comment}
                            hasArrow
                            placement="top"
                            openDelay={300}
                          >
                            <Text
                              color="gray.800"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                            >
                              {out.comment}
                            </Text>
                          </Tooltip>
                        ) : (
                          <Text color="gray.800">-</Text>
                        )}
                      </Td>
                      <Td fontSize="sm" color="gray.600">
                        {out.created_at
                          ? new Date(out.created_at).toLocaleString()
                          : "-"}
                      </Td>
                      <Td>
                        <Badge colorScheme={statusMeta.colorScheme}>
                          {statusMeta.label}
                        </Badge>
                      </Td>
                      <Td>
                        {out.approval_status !== "approved" ? (
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => handleOpenApproval(out)}
                          >
                            Action
                          </Button>
                        ) : (
                          "-"
                        )}
                      </Td>
                    </Tr>
                  );
                })
              ) : (
                <Tr>
                  <Td colSpan={7} textAlign="center" py={8} color="gray.500">
                    No outputs available for this version
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader>Add Output</DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Upload File
                </FormLabel>
                <Input
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Comment
                </FormLabel>
                <Textarea
                  placeholder="Enter your comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </FormControl>
            </VStack>
          </DrawerBody>
          <DrawerFooter>
            <Button
              variant="outline"
              mr={3}
              onClick={onClose}
              isDisabled={loading}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleAdd}
              isDisabled={!file}
              isLoading={loading}
              loadingText="Saving..."
              spinnerPlacement="start"
            >
              Save
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Approve / Reject Modal */}
      <ApprovalModal
        isOpen={isApprovalOpen}
        onClose={() => {
          if (!approving) {
            onApprovalClose();
            setSelectedOutput(null);
          }
        }}
        itemName={
          selectedOutput
            ? `${selectedOutput.original_name || "Output"}${
                selectedOutput.extension ? "." + selectedOutput.extension : ""
              }`
            : ""
        }
        loading={approving}
        onSubmit={handleApprovalSubmit}
      />

      {/* File Preview Modal */}
      <MediaPreviewModal
        isOpen={isPreviewOpen}
        onClose={onPreviewClose}
        title={previewFile?.title}
        url={previewFile?.url}
        isImage={previewFile?.isImage}
      />
    </Box>
  );
};

export default OutputSection;